import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

// ---------------------------------------------------------------------------
// useBusinessProfile
// Fetches an approved business profile and its active products by businessId.
// ---------------------------------------------------------------------------
export function useBusinessProfile(businessId) {
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!businessId) return;

    let cancelled = false;

    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch profile
        const profileSnap = await getDoc(doc(db, 'businessProfiles', businessId));
        if (!profileSnap.exists()) {
          if (!cancelled) setError('Brand not found.');
          return;
        }

        const profileData = { id: profileSnap.id, ...profileSnap.data() };

        if (profileData.status !== 'approved') {
          if (!cancelled) setError('Brand not found.');
          return;
        }

        // Fetch active products owned by this business
        const productsQuery = query(
          collection(db, 'products'),
          where('businessId', '==', businessId),
          where('catalogStatus', '==', 'active'),
          orderBy('createdAt', 'desc')
        );
        const productsSnap = await getDocs(productsQuery);
        const productsData = productsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        if (!cancelled) {
          setProfile(profileData);
          setProducts(productsData);
        }
      } catch (err) {
        console.error('useBusinessProfile error:', err);
        if (!cancelled) setError('Failed to load brand.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetch();
    return () => { cancelled = true; };
  }, [businessId]);

  return { profile, products, loading, error };
}
